import React, {useEffect, useState, useCallback, useMemo} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {View, Platform} from 'react-native';
import {Theme} from '../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Icon from '../../../components/icon';
import screens from '../../../config/screens';
import TextGeneric from '../../../components/textGeneric';
import {tr} from '../../../translations';
import {RootState, store} from '../../../redux/store';
import {useSelector, shallowEqual} from 'react-redux';
import {getHomeTabs} from './actions';
import {NewsItemType} from '../../../interfaces/api/tabs';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import EmptyList from '../../../components/emptyList';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import {navigationRef} from '../../../navigation';
import {ChatType, TabIds} from '../../../utils/enums';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  FadeOut,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import HomeTab from '../../../components/homeTab';
import HomeHeader from '../../../components/homeHeader';
import {
  homePopularTopicsTabs,
  homeQuickAccessTabs,
} from '../../../assets/static/homeTabs';
import GenioLatestNews from '../../../components/genioLatestNews';
import SearchListSkeleton from '../../../components/searchListSkeleton';
import {openChooseSpecialtyModal} from '../../../connected-components/chooseSpecialtyModal/actions';
import Config from 'react-native-config';
import {HomeTabItemType} from '../../../interfaces/reducers/homeState';
import QuickAccess from '../../../components/quickAccess';
import {ScrollView} from 'react-native-gesture-handler';
import {getHomeTabItemByKey} from '../../../utils/helpers';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import HomeTabsSkeleton from '../../../components/homeTabsSkeleton';
import {sendMessage} from '../chat/actions';
import {setCurrentChatType} from '../chat/reducer';

const Container = styled.View`
  width: 100%;
  flex: 1;
  position: relative;
`;
export const ColorOverlay = styled.View`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.secondary + '22'};
  z-index: 1;
`;
const GenioLatestNewsItem = styled(GenioLatestNews) <{ wdp: (percent: number) => number }>`
  width: ${Platform.OS === 'web' ? '94%' : ({ wdp }) => wdp(94) + 'px'};
  height: ${({ theme }) => theme.text.s1 * 3}px;
  align-self: center;
  margin-bottom: 3%;
`;
const PaddingView = styled.View<{ isMobile: boolean; width: number }>`
  width: 100%;
  max-width: ${({ isMobile }) => (isMobile ? '100%' : '1000px')};
  padding-horizontal: ${({ isMobile, width }) =>
    isMobile ? '3%' : width < 1100 ? '2%' : '8%'};
  align-self: center;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: ${({ isMobile, width }) => isMobile ? '2%' : width < 1100 ? '3%' : '2%'};
`;
const NewsContainer = styled.View`
  width: 100%;
  align-items: center;
`;
const SectionTitle = styled(TextGeneric) <{ paddingTop: number }>`
  color: ${({ theme }) => theme.text.dark};
  font-size: ${({ theme }) => theme.text.s4}px;
  padding-top: ${({ paddingTop }) => paddingTop}px;
  flex: 1;
`;
const Row = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-horizontal: 3%;
  align-self: center;
`;
export const Space = styled.View<{ bottomInset?: number; extraSpace?: number }>`
  align-self: center;
  width: 100%;
  height: ${({ bottomInset, extraSpace }) =>
    (bottomInset || 0) + (extraSpace || 0)}px;
`;
const ViewAllContainer = styled.TouchableOpacity`
  align-items: center;
  justify-content: center;
  flex-direction: row;
  white-space: nowrap;
`;
const ViewAll = styled(TextGeneric)`
  font-size: ${({ theme }) => theme.text.s7}px;
  color: ${({ theme }) => theme.text.dark};
  margin-right: ${({ theme }) => theme.text.s7}px;
  white-space: nowrap;
`;
export const EmptyContainer = styled.View<{ hdp: (percent: number) => number }>`
  min-height: ${Platform.OS === 'web' ? '75%' : ({ hdp }) => hdp(75) + 'px'};
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const EmptyContainerHome = styled(View) <{ width: number }>`
  width: ${({ width }) => width}px;
  min-height: 0;
  align-self: center;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 5%;
`;

// Web-specific CSS animations to prevent flashing
const webAnimationStyles = Platform.OS === 'web' ? `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .home-tab-animate {
    animation: fadeInUp 0.4s ease-out forwards;
    animation-fill-mode: both;
  }

  .home-content-animate {
    animation: fadeIn 0.3s ease-out forwards;
    animation-fill-mode: both;
  }

  .home-skeleton-animate {
    animation: fadeIn 0.2s ease-out forwards;
    animation-fill-mode: both;
  }
` : '';

// Inject CSS styles for web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const styleId = 'home-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = webAnimationStyles;
    document.head.appendChild(style);
  }
}

/**
 * Home screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const Home: React.FC<ScreenProps> = () => {
  const theme = useTheme() as Theme;
  const { wdp, hdp, getByScreenSize, width, isMobile, getResponsiveSize } = useResponsiveDimensions();
  const navigation = useNavigation<NavigationProp<any>>();
  const { top: topInset } = useSafeAreaInsets();

  const auth = useSelector((state: RootState) => state.auth);
  const currentUser = auth?.currentUser || {};

  const hideQuickAccess = Config.HIDE_QUICK_ACCESS?.toLowerCase() === 'true';
  const hideLatestNews = Config.HIDE_LATEST_NEWS?.toLowerCase() === 'true';
  const hideMedicalUpdates = Config.HIDE_MEDICAL_UPDATES?.toLowerCase() === 'true';

  const { tabs, latestNews, isLoading, isError } = useSelector(
    (state: RootState) => ({
      tabs: state.homeTabs.tabs,
      latestNews: state.homeTabs.latestNews,
      isLoading: state.homeTabs.isLoading,
      isError: state.homeTabs.isError,
    }),
    shallowEqual,
  );

  const homeTabs = useMemo(() => {
    // Only show Q&A, Clinical Cases, and Drugs & Diseases
    return homePopularTopicsTabs;
  }, []);

  const getTabWidth = useCallback(() => {
    return getResponsiveSize(45, 28, 26) + '%';
  }, [homeTabs.length, getResponsiveSize, width]);

  const isAppleUser = useMemo(() => {
    return currentUser?.email === Config.APPLE_EMAIL;
  }, [currentUser]);

  const [isNavigationReady, setIsNavigationReady] = useState(false);
  const scrollY = useSharedValue(0);
  const scrollOpacity = useSharedValue(Platform.OS === 'web' ? 1 : 0);
  const [webContentVisible, setWebContentVisible] = useState(Platform.OS === 'web');

  // Create stable scroll handler
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollY.value = event.contentOffset.y;
    },
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: scrollOpacity.value,
      flex: 1,
    };
  }, []);

  useEffect(() => {
    const onReady = () => {
      setIsNavigationReady(true);
    };

    if (navigationRef.current?.isReady()) {
      onReady();
    }

    const unsubscribe = navigationRef.current?.addListener('state', () => {
      if (navigationRef.current?.isReady()) {
        onReady();
      }
    });

    return () => {
      unsubscribe && unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      openChooseSpecialtyModal();
    }
  }, [currentUser]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      // Native: Use reanimated opacity
      scrollOpacity.value = withTiming(isLoading || isError ? 0 : 1, {
        duration: 300,
      });
    } else {
      // Web: Use state-based visibility to prevent flashing
      setWebContentVisible(!isLoading && !isError);
      scrollOpacity.value = 1; // Always 1 on web
    }
  }, [isLoading, isError]);

  useEffect(() => {
    if (Config.DEMO_EMAIL !== currentUser?.email) {
      if (
        !!currentUser &&
        currentUser?.is_first_login === 1 &&
        isNavigationReady
      ) {
        navigation?.navigate(screens.content.afterLogin);
      } else if (!!currentUser && currentUser?.is_first_login === 1) {
        navigation?.navigate(screens.content.afterLogin);
      }
    }
  }, [currentUser, isNavigationReady, navigation]);

  // Memoize callbacks to prevent recreation
  const onHomeTabPressHandler = useCallback(
    (item: HomeTabItemType) => {
      if (Array.isArray(item?.items)) {
        navigation?.navigate(screens.content.calculators, {
          data: item?.items,
        });
        return;
      }
      if (item?.has_topics) {
        navigation?.navigate(screens.content.topics, item);
        return;
      }
      if (item?.pdf) {
        navigation?.navigate(screens.content.fileViewer, item);
        return;
      }
      if (+(item?.id + '') === TabIds.Drugs) {
        navigation?.navigate(screens.content.drugsDiseases);
        return;
      }

      navigation?.navigate(screens.content.filterTabNews, item);
    },
    [navigation],
  );


  const onTabPressHandler = useCallback(
    (item: any) => {
      if (item?.label === 'medicalUpdates') {
        navigation?.navigate(screens.content.medicalUpdatesCongresses);
        return;
      }
      if (item?.label === 'qna') {
        store.dispatch(setCurrentChatType(ChatType.QNA));
        navigation?.navigate(screens.content.genioChatRoutes, {
          screen: screens.content.genioChat,
          params: { tab_id: TabIds.QA },
        });
        return;
      }
      if (item?.label === 'clinicalCase') {
        store.dispatch(setCurrentChatType(ChatType.ClinicalCase));
        navigation?.navigate(screens.content.genioChatRoutes, {
          screen: screens.content.enterClinicalCase,
          params: { tab_id: TabIds.ClinicalCases },
        });
        return;
      }
      if (item?.label === 'medicalReports') {
        store.dispatch(setCurrentChatType(ChatType.ClinicalCase));
        navigation?.navigate(screens.content.genioChatRoutes, {
          screen: screens.content.enterClinicalCase,
          params: { tab_id: TabIds.MedicalReports },
        });
        return;
      }
      if (item?.label === 'drugsAndDiseases') {
        navigation?.navigate(screens.content.drugsDiseases);
        return;
      }
      if (
        item?.label === 'medicalCalculators' ||
        item?.label === 'laboratoryValues' ||
        item?.label === 'latestInnovations'
      ) {
        const data = getHomeTabItemByKey(item.key);
        onHomeTabPressHandler(data);
        return;
      }
      onHomeTabPressHandler(item);
    },
    [navigation, onHomeTabPressHandler],
  );


  const _renderRecommendationItem = useCallback(
    (item: NewsItemType, index: number) => {
      return (
        <GenioLatestNewsItem
          key={item.id}
          index={index}
          wdp={wdp}
          title={item?.title || ''}
          specialty={item?.subspeciality || item?.specialty || ''}
          image={{ uri: item?.img || '' }}
          onPress={() =>
            navigation?.navigate(screens.content.latestArticle, {
              item,
              isHomeRecommendation: true,
            })
          }
        />
      );
    },
    [navigation],
  );

  const _renderEmptyRecommendations = useCallback(() => {
    if (isError) {
      return (
        <EmptyContainerHome width={wdp(94)}>
          <EmptyList
            iconName={'alert'}
            iconSize={theme.text.s1 * 2.5}
            onRefreshPress={getHomeTabs}
            title={'app.error'}
            showRefresh
            containerStyle={{
              backgroundColor: theme.text.light,
            }}
          />
        </EmptyContainerHome>
      );
    }
    if (isLoading) {
      return (
        <Animated.View
          style={{ width: '94%', alignSelf: 'center' }}
          entering={FadeIn}
          exiting={FadeOut}>
          <SearchListSkeleton isSmall />
        </Animated.View>
      );
    }
    return null;
  }, [isError, isLoading, wdp, theme]);

  const _renderQuickAccessItem = useCallback(
    (
      item: {
        id: string | number;
        label: string;
        img_url?: string;
        key?: string;
      },
      index: number,
    ) => {
      return (
        <Animated.View
          key={item.id}
          style={{
            width: wdp(getByScreenSize(40, 12)),
            marginLeft: getByScreenSize(wdp(3), wdp(0.5)),
          }}
          entering={FadeInDown.delay(index * 50)}
          exiting={FadeOut}>
          <QuickAccess
            title={tr(`home.${item.label}`)}
            onPress={() => onTabPressHandler(item)}
            imageUri={item.img_url}
            defaultImage={
              homeQuickAccessTabs[index % homeQuickAccessTabs.length].img
            }
          />
        </Animated.View>
      );
    },
    [onTabPressHandler, wdp, getByScreenSize],
  );

  // Stabilize the animation configurations to prevent unnecessary recreations
  const stableAnimations = useMemo(() => {
    // Create an object with all the animations we need to reduce re-creations
    return {
      fadeIn: FadeIn.duration(300),
      fadeOut: FadeOut.duration(200),
      // Pre-compute delayed animations at component mount time - use FadeInUp on web to prevent flashing
      delayedAnimations: Array.from({ length: 20 }).map((_, i: number) =>
        Platform.OS === 'web'
          ? FadeInUp.delay(i * 30).duration(400).springify().damping(15)
          : FadeInDown.delay(i * 50).duration(300),
      ),
    };
  }, []);

  // Fix the render section of the component
  return (
    <MainLayout
      showOverlay={false}
      enableScroll={false}
      isCustomHeader
      noPadding
      noPaddingHeader>
      <Container>
        <HomeHeader />
        {isError ? (
          <EmptyContainer
            hdp={hdp}
            style={{
              paddingTop: theme.text.s55,
              width: getByScreenSize('90%', '40%'),
              alignSelf: 'center',
            }}>
            <EmptyList
              iconName={'alert'}
              iconSize={theme.text.s1 * 2.5}
              onRefreshPress={getHomeTabs}
              title={'app.error'}
              showRefresh
              containerStyle={{ backgroundColor: theme.cardBackground }}
            />
          </EmptyContainer>
        ) : (
          isLoading && (
            Platform.OS === 'web' ? (
              <div
                className="home-skeleton-animate"
                style={{
                  paddingTop: theme.text.s55,
                  pointerEvents: 'none' as const,
                  width: '100%',
                  alignSelf: 'center' as const,
                  display: 'flex' as const,
                  justifyContent: 'center' as const
                }}>
                <HomeTabsSkeleton
                  style={{
                    width: '100%',
                    alignSelf: 'center',
                  }}
                />
              </div>
            ) : (
              <Animated.View
                pointerEvents={'none'}
                style={{ paddingTop: theme.text.s55 }}
                entering={FadeInDown}
                exiting={FadeOut.duration(150)}>
                <HomeTabsSkeleton
                  style={{
                    width: '100%',
                    alignSelf: 'center',
                  }}
                />
              </Animated.View>
            )
          )
        )}
        {Platform.OS === 'web' && webContentVisible && (
          <div
            className="home-content-animate"
            style={{
              flex: 1,
              maxWidth: isMobile ? '100%' : width < 1000 ? '100%' : 1000 + 'px',
              minWidth: isMobile ? '100%' : width < 1000 ? '100%' : 1000 + 'px',
              alignSelf: 'center' as const,
              overflowY: 'auto' as const,
              pointerEvents: isLoading || isError ? 'none' : 'auto',
            }}>
            <div style={{ display: 'block' }}>
              <PaddingView isMobile={isMobile} width={width} style={{ paddingTop: theme.text.s55 }}>
                {homeTabs.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="home-tab-animate"
                    style={{
                      width: getTabWidth(),
                      marginBottom: '2%',
                      animationDelay: `${index * 30}ms`,
                    }}>
                    <HomeTab
                      title={tr(`home.${item.label}`)}
                      onPress={() => onTabPressHandler(item)}
                      defaultImage={item.img}
                      isPrimary={false}
                    />
                  </div>
                ))}
              </PaddingView>

              {!hideQuickAccess && !isAppleUser &&
                (currentUser?.profession_name + '').toLowerCase() ===
                'physician' && (
                  <>
                    <Row>
                      <SectionTitle
                        semiBold
                        style={{ paddingBottom: hdp(3) }}
                        paddingTop={hdp(3)}>
                        {tr('home.quickAccess')}
                      </SectionTitle>
                      <ViewAllContainer
                        onPress={() =>
                          navigation?.navigate(screens.content.medicalUpdates)
                        }>
                        <ViewAll bold>{tr('app.viewAll')}</ViewAll>
                        <Icon name={'navArrow'} size={theme.text.s1} />
                      </ViewAllContainer>
                    </Row>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      style={{ flexGrow: 1, width: wdp(100) }}
                      contentContainerStyle={{ paddingEnd: wdp(3) }}>
                      {(homeQuickAccessTabs || []).map(_renderQuickAccessItem)}
                    </ScrollView>
                  </>
                )}

              {!hideLatestNews && !isAppleUser && (
                <>
                  {(latestNews.length !== 0 || isLoading) && (
                    <Row>
                      <SectionTitle
                        semiBold
                        paddingTop={hdp(3)}
                        style={{ paddingBottom: hdp(3) }}>
                        {tr('news.latestNewsTitle')}
                      </SectionTitle>
                      {latestNews.length > 3 && (
                        <ViewAllContainer
                          onPress={() =>
                            navigation?.navigate(screens.content.news, {
                              isHomeRecommendation: true,
                            })
                          }>
                          <ViewAll bold>{tr('app.viewAll')}</ViewAll>
                          <Icon name={'navArrow'} size={theme.text.s1} />
                        </ViewAllContainer>
                      )}
                    </Row>
                  )}
                  <NewsContainer>
                    {latestNews.length === 0
                      ? _renderEmptyRecommendations()
                      : (latestNews || [])
                        .slice(0, 3)
                        .map(_renderRecommendationItem)}
                  </NewsContainer>
                </>
              )}
              <Space bottomInset={topInset} extraSpace={hdp(15)} />
            </div>
          </div>
        )}
        {Platform.OS !== 'web' && (
          <Animated.ScrollView
            scrollEnabled={!(isLoading || isError)}
            showsVerticalScrollIndicator={false}
            style={animatedStyle}
            pointerEvents={isLoading || isError ? 'none' : 'auto'}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            //@ts-ignore
            contentContainerStyle={{
              pointerEvents: isLoading || isError ? 'none' : 'auto',
              maxWidth: '100%',
              alignSelf: 'center',
            }}>
            <PaddingView isMobile={isMobile} width={width} style={{ paddingTop: theme.text.s55 }}>
              {[...homeTabs, ...homeTabs].map((item, index) => (
                <Animated.View
                  key={`${item.id}-${index}`}
                  style={{
                    width: getTabWidth() as any,
                    marginBottom: '2%',
                  }}
                  entering={stableAnimations.delayedAnimations[index % 20]}
                  exiting={stableAnimations.fadeOut}>
                  <HomeTab
                    title={tr(`home.${item.label}`)}
                    onPress={() => onTabPressHandler(item)}
                    defaultImage={item.img}
                    isPrimary={false}
                  />
                </Animated.View>
              ))}
            </PaddingView>

            {!hideQuickAccess && !isAppleUser &&
              (currentUser?.profession_name + '').toLowerCase() ===
              'physician' && (
                <>
                  <Row>
                    <SectionTitle
                      semiBold
                      style={{ paddingBottom: hdp(3) }}
                      paddingTop={hdp(3)}>
                      {tr('home.quickAccess')}
                    </SectionTitle>
                    <ViewAllContainer
                      onPress={() =>
                        navigation?.navigate(screens.content.medicalUpdates)
                      }>
                      <ViewAll bold>{tr('app.viewAll')}</ViewAll>
                      <Icon name={'navArrow'} size={theme.text.s1} />
                    </ViewAllContainer>
                  </Row>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={{ flexGrow: 1, width: wdp(100) }}
                    contentContainerStyle={{ paddingEnd: wdp(3) }}>
                    {(homeQuickAccessTabs || []).map(_renderQuickAccessItem)}
                  </ScrollView>
                </>
              )}

            {!hideLatestNews && !isAppleUser && (
              <>
                {(latestNews.length !== 0 || isLoading) && (
                  <Row>
                    <SectionTitle
                      semiBold
                      paddingTop={hdp(3)}
                      style={{ paddingBottom: hdp(3) }}>
                      {tr('news.latestNewsTitle')}
                    </SectionTitle>
                    {latestNews.length > 3 && (
                      <ViewAllContainer
                        onPress={() =>
                          navigation?.navigate(screens.content.news, {
                            isHomeRecommendation: true,
                          })
                        }>
                        <ViewAll bold>{tr('app.viewAll')}</ViewAll>
                        <Icon name={'navArrow'} size={theme.text.s1} />
                      </ViewAllContainer>
                    )}
                  </Row>
                )}
                <NewsContainer>
                  {latestNews.length === 0
                    ? _renderEmptyRecommendations()
                    : (latestNews || [])
                      .slice(0, 3)
                      .map(_renderRecommendationItem)}
                </NewsContainer>
              </>
            )}
            <Space bottomInset={topInset} extraSpace={hdp(15)} />
          </Animated.ScrollView>
        )}
      </Container>
    </MainLayout>
  );
};

Home.displayName = 'HomeScreen';
export default React.memo(Home);
